import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ImagePlus } from "lucide-react";
import { ACCEPT, api, ensureGuest, errMsg, MSG, pendingUploads } from "@/lib/api";
import { PermissionCheck } from "@/components/Provenance";
import { ImportSources } from "@/components/ImportSources";

export const ImportPanel = ({ urlRef }) => {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(false);

  const submitUrl = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setError("");
    try {
      await ensureGuest();
      const { data } = await api.post("/properties", { listingUrl: url.trim() });
      nav(`/p/${data.id}`);
    } catch (err) {
      setError(errMsg(err, MSG.zillow));
      setBusy(false);
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      await ensureGuest();
      const { data } = await api.post("/properties", {});
      pendingUploads.set(data.id, files);
      nav(`/p/${data.id}`);
    } catch (err) {
      setError(errMsg(err, "We couldn't start a workspace. Please try again."));
      setBusy(false);
    }
  };

  return (
    <section id="import" data-testid="import-panel" className="rounded-xl border border-[var(--line)] bg-[var(--bone-2)]/95 p-5 shadow-[0_30px_80px_-40px_rgba(31,28,25,.35)] backdrop-blur sm:p-8">
      <h2 data-testid="import-panel-heading" className="font-display text-[1.7rem] leading-tight tracking-tight sm:text-3xl">
        What could this place become?
      </h2>
      <form onSubmit={submitUrl} className="mt-4 flex flex-col gap-2 sm:flex-row" data-testid="zillow-import-form">
        <input
          ref={urlRef}
          value={url}
          onChange={(e) => { setUrl(e.target.value); setError(""); }}
          placeholder="Paste a Zillow listing URL"
          inputMode="url"
          type="url"
          enterKeyHint="go"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="field"
          data-testid="zillow-url-input"
        />
        <button type="submit" disabled={busy || !url.trim()} className="btn-primary shrink-0" data-testid="zillow-submit-button">
          Import <ArrowRight size={16} />
        </button>
      </form>
      {error && <p data-testid="import-error" role="alert" className="mt-2 text-sm text-[#B42318]">{error}</p>}
      <div className="my-4 flex items-center gap-3 text-[var(--stone-dark)]">
        <span className="h-px flex-1 bg-[var(--line)]" /><span className="eyebrow">or</span><span className="h-px flex-1 bg-[var(--line)]" />
      </div>
      <PermissionCheck checked={perm} onChange={setPerm} />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy || !perm} className="btn-ghost mt-2 w-full" data-testid="upload-photos-button">
        <ImagePlus size={17} /> Upload property photographs
      </button>
      <input ref={fileRef} type="file" multiple accept={ACCEPT} onChange={onFiles} className="hidden" data-testid="photo-upload-input" />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--stone-dark)]"><span>JPEG, PNG, WebP or HEIC · up to 20 photos · no sign-up</span><ImportSources /></div>
    </section>
  );
};
